import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import LoginRoleToggle from './LoginRoleToggle';
import WrongPortalHint from './WrongPortalHint';

const Login = ({ onLogin }) => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [isRegister, setIsRegister] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: searchParams.get('email') || '',
    password: '',
  });

  // Keep email synced if it arrives later (e.g. after a redirect from /client with ?email=...)
  useEffect(() => {
    const qsEmail = searchParams.get('email');
    if (qsEmail && !formData.email) {
      setFormData((f) => ({ ...f, email: qsEmail }));
    }
  }, [searchParams, formData.email]);
  const [loading, setLoading] = useState(false);

  const languages = [
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  ];

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('language', langCode);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isRegister ? '/auth/register' : '/auth/login';
      // Normalize email to lowercase
      const normalizedEmail = formData.email.trim().toLowerCase();
      
      const payload = isRegister
        ? { name: formData.name, email: normalizedEmail, password: formData.password, is_admin: false }
        : { email: normalizedEmail, password: formData.password };

      const response = await axiosInstance.post(endpoint, payload);
      onLogin(response.data.token, response.data.user);
      toast.success(isRegister ? t('success') : t('success'));
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else if (error.code === 'ERR_NETWORK' || !error.response) {
        toast.error('Erro de conexão com o servidor. Verifique sua internet e tente novamente.');
      } else if (error.code === 'ECONNABORTED') {
        toast.error('Servidor demorou para responder. Tente novamente.');
      } else {
        toast.error(t('error'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0d3d3d] to-[#1a5555] p-4">
      {/* Language Selector - Top Right */}
      <div className="absolute top-4 right-4">
        <div className="flex items-center gap-2 bg-white/10 backdrop-blur-lg rounded-full px-4 py-2 border border-white/20">
          <Globe className="w-4 h-4 text-white/70" />
          <div className="flex gap-1">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-300 ${
                  i18n.language === lang.code
                    ? 'bg-[#3da5a5] text-white scale-105 shadow-lg'
                    : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
                title={lang.name}
              >
                <span className="mr-1">{lang.flag}</span>
                <span className="hidden sm:inline">{lang.code.toUpperCase()}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="w-full max-w-md">
        <div className="flex justify-center mb-4">
          <LoginRoleToggle />
        </div>
        {!isRegister && (
          <div className="mb-3"><WrongPortalHint email={formData.email} expected="medewerker" /></div>
        )}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/20">
          <div className="flex justify-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_dreamy-mcnulty-2/artifacts/39goexey_JPEG-afbeelding-4738-BFE8-D0-0.jpeg"
              alt="Cardoso Bouw"
              className="h-16 object-contain"
            />
          </div>
          
          <h2 className="text-3xl font-bold text-white text-center mb-2">
            {isRegister ? t('register') : t('welcome')}
          </h2>
          <p className="text-white/70 text-center mb-6">
            {t('administrativeDashboard')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div>
                <Label htmlFor="name" className="text-white mb-2 block">
                  {t('name')}
                </Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-white/10 border-white/30 text-white placeholder:text-white/50"
                  placeholder={t('name')}
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-white mb-2 block">
                {t('email')}
              </Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-white/10 border-white/30 text-white placeholder:text-white/50"
                placeholder={t('email')}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="password" className="text-white">
                  {t('password')}
                </Label>
                {!isRegister && (
                  <Link to={`/forgot-password?email=${encodeURIComponent(formData.email)}`} className="text-xs text-[#3da5a5] hover:text-white transition-colors" data-testid="forgot-password-link">
                    Wachtwoord vergeten?
                  </Link>
                )}
              </div>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="bg-white/10 border-white/30 text-white placeholder:text-white/50"
                placeholder="••••••••"
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full bg-[#3da5a5] hover:bg-[#2d8585] text-white font-semibold py-6 rounded-xl transition-all duration-300 hover:scale-[1.02]"
            >
              {loading ? t('loading') : isRegister ? t('register') : t('login')}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsRegister(!isRegister)}
              className="text-[#3da5a5] hover:text-[#2d8585] font-medium transition-colors"
              data-testid="toggle-auth-mode"
            >
              {isRegister ? t('login') : t('noAccount') + ' ' + t('register')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;